import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { addtoCart } from "../../features/cartSlice";
import { api } from "../Constants";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import Breadcrump from "../Breadcrump/Breadcrump";
import Image1 from "../../assets/new-images/svg_logo.svg";

function Category() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const userid = localStorage.getItem("userid");

  // --- STATE MANAGEMENT ---
  const [loading, setLoading] = useState(true);
  const [productdetails, setProductDetails] = useState([]);
  const [categorylist, setCategorylist] = useState([]);
  const [keywordCategories, setKeywordCategories] = useState([]);
  const [heading, setHeading] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [checkedKeywords, setCheckedKeywords] = useState([]);
  const [checkedSKUs, setCheckedSKUs] = useState([]);
  const [categorySearchText, setCategorySearchText] = useState("");
  const [openIndex, setOpenIndex] = useState(null);

  // Initialize currentPage to 1. useEffect will set the stored page.
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 9;

  // Modal states
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    message: "",
    subject: "Product Enquiry",
  });
  const [cartformData, setCartFormData] = useState({
    qty: "",
    sizes: "",
    carat: "",
    weight: "",
    remarks: "",
    product_id: null,
    product_code: "",
    quantity_variant: "",
  });

  // --- DATA FETCHING & STATE SYNC ---

  // This useEffect reads the page number from localStorage when you return to the page.
  useEffect(() => {
    const savedPage = localStorage.getItem('collectionPageNumber');
    if (savedPage) {
      setCurrentPage(Number(savedPage));
      localStorage.removeItem('collectionPageNumber'); // Clean up after use
    }
  }, []); // Empty array ensures this runs only once on component mount

  // Fetch main category list for sidebar
  useEffect(() => {
    axios
      .post(api + "category_list", { dealer_id: userid })
      .then((response) => {
        if (response.data && response.data.data) {
          setCategorylist(response.data.data);
        }
      })
      .catch((error) => console.error("Error fetching category list:", error));
  }, [userid]);

  // Fetch keyword list for filter accordion
  useEffect(() => {
    axios
      .get(api + "apiKeywordList")
      .then((res) => {
        if (res.data && res.data.data) {
          setKeywordCategories(res.data.data);
        }
      })
      .catch((error) => console.log(error));
  }, []);

  // Fetch products based on URL slug or keyword checkboxes
  useEffect(() => {
    const slug = params?.slug || "";
    const keywords = checkedKeywords;

    const isKeywordSearch = keywords.length > 0;
    const url = isKeywordSearch
      ? api + "search_categorykeyword"
      : api + "search_productcategory";
    const payload = isKeywordSearch
      ? { keywords: keywords, dealer_id: userid }
      : { category: slug, dealer_id: userid };

    setLoading(true);
    axios
      .post(url, payload)
      .then((res) => {
        setProductDetails(res.data.data || []);
        setHeading(res.data.heading || "");
      })
      .catch((error) => {
        console.error("API Error:", error);
        setProductDetails([]);
      })
      .finally(() => setLoading(false));

    setSelectedCategories(slug ? slug.split(",") : []);
    
  }, [params?.slug, checkedKeywords, userid]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, currentPage]);

  // --- FILTERING & PAGINATION LOGIC ---

  const finalFilteredProducts = useMemo(() => {
    let filtered = productdetails;
    if (checkedSKUs.length > 0) {
      filtered = filtered.filter((product) =>
        checkedSKUs.includes(product.product_code)
      );
    }
    return filtered;
  }, [productdetails, checkedSKUs]);

  const pageCount = Math.ceil(finalFilteredProducts.length / productsPerPage);
  const currentPageProducts = finalFilteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );
  const noResults = !loading && finalFilteredProducts.length === 0;
  
  // This effect ensures that if filters reduce the number of pages,
  // we don't stay on a non-existent page.
  useEffect(() => {
    if (!loading && pageCount > 0 && currentPage > pageCount) {
        setCurrentPage(pageCount);
    }
  }, [loading, currentPage, pageCount]);


  // --- EVENT HANDLERS ---

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setCurrentPage(newPage);
    }
  };
  
  // This helper function resets the page number when any filter is applied.
  const handleFilterChange = () => {
      setCurrentPage(1);
  }

  const handleCategoryCheckboxChange = (e, slug) => {
    const isChecked = e.target.checked;
    const updated = isChecked
      ? [...selectedCategories, slug]
      : selectedCategories.filter((item) => item !== slug);

    setCheckedKeywords([]);
    setCheckedSKUs([]);
    
    const path = updated.length > 0 ? `/category/${updated.join(",")}` : "/category";
    navigate(path);
    handleFilterChange();
  };

  const handleKeywordChange = (e, keywordId) => {
    handleFilterChange();
    const isChecked = e.target.checked;
    const updated = isChecked
      ? [...checkedKeywords, keywordId]
      : checkedKeywords.filter((id) => id !== keywordId);
    
    setCheckedKeywords(updated);
    setCheckedSKUs([]);

    if (updated.length > 0 && params.slug) {
        navigate('/category');
    }
  };
  
  const handleSKUChange = (e, product_code) => {
    const isChecked = e.target.checked;
    setCheckedSKUs((prev) =>
      isChecked
        ? [...prev, product_code]
        : prev.filter((code) => code !== product_code)
    );
    handleFilterChange();
  };
  
  const handleSelectAllCategories = () => {
    handleFilterChange();
    setSelectedCategories([]);
    setCheckedKeywords([]);
    setCheckedSKUs([]);
    navigate("/category");
  };

  const handleSearchChange = (text) => {
    setCategorySearchText(text);
    const lowerSearch = text.toLowerCase();

    if (!text) {
        setOpenIndex(null);
        return;
    }

    const keywordMatchIndex = keywordCategories.findIndex(
      (item) =>
        item.keyword_category_name.toLowerCase().includes(lowerSearch) ||
        item.keywords.some((kw) => kw.keyword.toLowerCase().includes(lowerSearch))
    );
    
    const skuMatch = productdetails.some(p => p.product_code.toLowerCase().includes(lowerSearch));

    if (keywordMatchIndex !== -1) {
      setOpenIndex(keywordMatchIndex);
    } else if (skuMatch) {
      setOpenIndex('sku');
    } else {
      setOpenIndex(null);
    }
  };

  const handleShowEnquiry = (item) => {
    setFormData({
      ...formData,
      product_code: item.product_code,
      product_id: item.product_id,
      dealer_id: userid,
    });
    setShowEnquiryModal(true);
  };

  const handleCloseEnquiry = () => setShowEnquiryModal(false);

  const handleEnquirySubmit = () => {
    if (Object.values(formData).some(val => val === "")) {
        alert("Please fill all fields.");
        return;
    }
    console.log("Submitting enquiry:", formData);
    handleCloseEnquiry();
  };

  const handleShowCart = (item) => {
    setCartFormData((prev) => ({
      ...prev,
      product_id: item.product_id,
      product_code: item.product_code,
      quantity_variant: item.quantity_variant,
    }));
    setShowCartModal(true);
  };

  const handleCloseCart = () => setShowCartModal(false);

  const handleCartSubmit = async (e) => {
    e.preventDefault();
    if (!userid) return;
    
    const { product_id, qty, sizes, carat, weight, remarks } = cartformData;
    try {
      await dispatch(
        addtoCart({
          product_id: parseInt(product_id),
          userids: userid,
          qty: parseInt(qty),
          size: sizes,
          carat,
          weight,
          remarks,
        })
      ).unwrap();

      handleCloseCart();
      setCartFormData({
        qty: "", sizes: "", carat: "", weight: "", remarks: "",
        product_id: null, product_code: "", quantity_variant: ""
      });
    } catch (error) {
      console.error("Failed to add to cart:", error);
    }
  };

  const handleFormChange = (e, formSetter) => {
      const { name, value } = e.target;
      formSetter(prev => ({ ...prev, [name]: value }));
  }

  // Helper function to save the page number before navigating to a product detail page
  const savePageAndNavigate = () => {
    localStorage.setItem('collectionPageNumber', currentPage);
  };


  return (
    <>
      {loading ? (
        <div className="shop-main-wrapper section-padding" style={{ height: "100vh", background: "#fff" }}>
          <div className="container h-100">
            <div className="row h-100">
              <div
                className="col-12 d-flex flex-column justify-content-center align-items-center"
                style={{ height: "100%" }}
              >
                <img
                  src={Image1}
                  alt="Loading..."
                  style={{
                    width: "320px",
                    height: "auto",
                    marginBottom: "30px",
                    animation: "pulse 1.5s infinite ease-in-out",
                  }}
                />
                <div className="dot-spinner">
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                    <div className="dot-spinner__dot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Header />
          <Breadcrump cont2="Collection" />
          <div className="shop-main-wrapper section-padding">
            <div className="container">
              <div className="row">
                {/* --- SIDEBAR --- */}
                <div className="col-lg-3 left_col">
                  <div className="inner-box">
                    <h4 className="mb-4" style={{ color: "#bc1823" }}>
                      Categories
                    </h4>
                    <ul>
                      <li>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <input
                            type="checkbox"
                            onChange={handleSelectAllCategories}
                            checked={!params.slug && checkedKeywords.length === 0}
                          />
                          <p style={{ fontSize: "20px", cursor: "pointer", marginBottom: "0" }} onClick={handleSelectAllCategories}>All Products</p>
                        </div>
                      </li>
                      {categorylist.map((item, index) => (
                        <li key={index}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(item.category_slug)}
                              onChange={(e) => handleCategoryCheckboxChange(e, item.category_slug)}
                            />
                            <Link
                              to={`/category/${item.category_slug}`}
                              style={{
                                fontSize: "20px",
                                color: selectedCategories.includes(item.category_slug) ? "#bc1823" : "inherit",
                              }}
                            >
                              {item.name}
                            </Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="inner-box mt-4">
                    <input
                      type="text"
                      placeholder="Search Filters..."
                      value={categorySearchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      style={{ marginBottom: "1.5rem", padding: "8px", width: "100%" }}
                    />
                    {keywordCategories.filter(item => 
                        item.keyword_category_name.toLowerCase().includes(categorySearchText.toLowerCase()) ||
                        item.keywords.some(kw => kw.keyword.toLowerCase().includes(categorySearchText.toLowerCase()))
                      ).map((item, index) => (
                      <div key={item.keyword_category_id}>
                        <div
                          onClick={() => setOpenIndex(openIndex === index ? null : index)}
                          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#bc1823", marginBottom: "1.5rem" }}
                        >
                          <h4 style={{ fontWeight: "inherit" }}>{item.keyword_category_name}</h4>
                          <span>{openIndex === index ? "▲" : "▼"}</span>
                        </div>
                        {openIndex === index && (
                          <ul className="mb-4">
                            {item.keywords.filter(kw => kw.keyword.toLowerCase().includes(categorySearchText.toLowerCase())).map((kwItem) => (
                              <li key={kwItem.keyword_id}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <input
                                    type="checkbox"
                                    onChange={(e) => handleKeywordChange(e, kwItem.keyword_id)}
                                    checked={checkedKeywords.includes(kwItem.keyword_id)}
                                    value={kwItem.keyword_id}
                                  />
                                  <p style={{ fontSize: "20px", marginBottom: "0" }}>{kwItem.keyword}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                    <div>
                        <div
                          onClick={() => setOpenIndex(openIndex === 'sku' ? null : 'sku')}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            color: "#bc1823",
                            marginBottom: "1.5rem",
                          }}
                        >
                          <h4 style={{ fontWeight: "inherit" }}>SKU</h4>
                          <span>{openIndex === 'sku' ? "▲" : "▼"}</span>
                        </div>
                        {openIndex === 'sku' && (
                          <ul className="mb-4" style={{maxHeight: '200px', overflowY: 'auto'}}>
                              {productdetails
                                .filter(product => product.product_code.toLowerCase().includes(categorySearchText.toLowerCase()))
                                .map((product, i) => (
                                  <li key={i}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                          <input
                                              type="checkbox"
                                              onChange={(e) => handleSKUChange(e, product.product_code)}
                                              checked={checkedSKUs.includes(product.product_code)}
                                              value={product.product_code}
                                          />
                                          <p style={{ fontSize: "18px", marginBottom: "0" }}>
                                              {product.product_code}
                                          </p>
                                      </div>
                                  </li>
                                ))
                              }
                          </ul>
                        )}
                    </div>
                  </div>
                </div>

                {/* --- MAIN CONTENT --- */}
                <div className="col-lg-9">
                  <div className="shop-product-wrapper">
                    <div className="shop-top-bar">
                      <div className="row align-items-center">
                        <div className="col-lg-5 col-md-6 order-md-1">
                          <div className="top-bar-left">
                            <div className="product-amount">
                              {finalFilteredProducts.length > 0 && <h4>{heading}</h4>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="shop-product-wrap grid-view row mbn-30">
                      {noResults && !loading ? (
                        <div className="col-md-12 text-center">
                          <p>No Results Found</p>
                        </div>
                      ) : (
                        currentPageProducts.map((detailsone) => (
                          <div className="col-md-4 col-sm-6" key={detailsone.product_id}>
                            <div className="product-item">
                              <figure className="product-thumb">
                                <Link to={`/product-details/${detailsone.prd_slug_name}`} onClick={savePageAndNavigate}>
                                  <img
                                    className="pri-img our-products-home"
                                    src={detailsone.product_image}
                                    alt="product"
                                  />
                                </Link>
                                <div className="cart-hover">
                                  {userid ? (
                                    <button className="btn btn-cart" onClick={() => handleShowCart(detailsone)}>
                                      Add to Cart
                                    </button>
                                  ) : (
                                    <button className="btn btn-cart" onClick={() => handleShowEnquiry(detailsone)}>
                                      Enquiry Now
                                    </button>
                                  )}
                                </div>
                              </figure>
                              <div className="product-caption text-center">
                                <h6 className="product-name">
                                  <Link to={`/product-details/${detailsone.prd_slug_name}`} onClick={savePageAndNavigate}>
                                    {detailsone.category_name}
                                  </Link>
                                </h6>
                                <div className="product-identity">
                                  <p className="manufacturer-name">
                                    Qty :
                                    <span className="price-regular" style={{ paddingLeft: "3px" }}>
                                      {detailsone.quantity}{" "}
                                      <span style={{ paddingLeft: "3px" }}>
                                        {detailsone.quantity_variant}
                                      </span>
                                    </span>
                                  </p>
                                </div>
                                <div className="product-identity">
                                  <p className="manufacturer-name">
                                    Weight :
                                    <span className="price-regular" style={{ paddingLeft: "3px" }}>
                                      {detailsone.weight}
                                      <span style={{ paddingLeft: "3px" }}>g</span>
                                    </span>
                                  </p>
                                </div>
                                <div className="price-box">
                                  <span className="price-old">SKU : </span>
                                  <span className="price-regular">{detailsone.product_code}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* --- PAGINATION --- */}
                    {!noResults && pageCount > 1 && (
                      <div className="paginatoin-area text-center">
                        <ul className="pagination-box">
                          <li>
                            <button
                              className="previous"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <i className="pe-7s-angle-left"></i>
                            </button>
                          </li>
                          {[...Array(pageCount).keys()].map((page) => (
                            <li key={page + 1} className={currentPage === page + 1 ? "active" : ""}>
                              <button onClick={() => handlePageChange(page + 1)}>
                                {page + 1}
                              </button>
                            </li>
                          ))}
                          <li>
                            <button
                              className="next"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === pageCount}
                            >
                              <i className="pe-7s-angle-right"></i>
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />

          {/* --- MODALS --- */}
          <Modal show={showEnquiryModal} onHide={handleCloseEnquiry} centered>
               <Modal.Header closeButton>
                 <Modal.Title>Product Enquiry</Modal.Title>
               </Modal.Header>
               <Modal.Body>
                 <form onSubmit={(e) => e.preventDefault()}>
                   <div className="row g-3">
                     <div className="col-12 mb-3">
                       <input type="text" className="form-control" placeholder="Your Name" name="name" value={formData.name} onChange={(e) => handleFormChange(e, setFormData)} required />
                     </div>
                     <div className="col-12 mb-3">
                       <input type="text" className="form-control" placeholder="Phone Number" name="mobile" value={formData.mobile} onChange={(e) => handleFormChange(e, setFormData)} maxLength="10" required />
                     </div>
                     <div className="col-12 mb-3">
                       <input type="email" className="form-control" placeholder="Email Address" name="email" value={formData.email} onChange={(e) => handleFormChange(e, setFormData)} required />
                     </div>
                     <div className="col-12 mb-3">
                       <input type="text" className="form-control" placeholder="Your City" name="city" value={formData.city} onChange={(e) => handleFormChange(e, setFormData)} required />
                     </div>
                     <div className="col-12 mb-3">
                       <textarea className="form-control" placeholder="Message" name="message" value={formData.message} onChange={(e) => handleFormChange(e, setFormData)} required></textarea>
                     </div>
                   </div>
                 </form>
               </Modal.Body>
               <Modal.Footer>
                 <button className="main-btn ml2 closebtn" onClick={handleCloseEnquiry}>Close</button>
                 <button className="main-btn mr2" onClick={handleEnquirySubmit}>Submit</button>
               </Modal.Footer>
          </Modal>

          <Modal show={showCartModal} onHide={handleCloseCart} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add to Cart</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleCartSubmit}>
                    <div className="row g-3">
                        <div className="col-12 mb-3">
                            <div className="input-group">
                                <input type="number" className="form-control" placeholder="Quantity" name="qty" value={cartformData.qty} onChange={(e) => handleFormChange(e, setCartFormData)} required />
                                <span className="input-group-text">{cartformData.quantity_variant}</span>
                            </div>
                        </div>
                        <div className="col-12 mb-3">
                            <input type="text" className="form-control" placeholder="Sizes" name="sizes" value={cartformData.sizes} onChange={(e) => handleFormChange(e, setCartFormData)} />
                        </div>
                        <div className="col-12 mb-3">
                            <select className="form-select" name="carat" value={cartformData.carat} onChange={(e) => handleFormChange(e, setCartFormData)} required>
                                <option value="" disabled>Select Carat</option>
                                <option value="22 KT">22 KT</option>
                                <option value="18 KT Yellow Gold">18 KT Yellow Gold</option>
                                <option value="18 KT Rose Gold">18 KT Rose Gold</option>
                                <option value="18 KT White Gold">18 KT White Gold</option>
                            </select>
                        </div>
                        <div className="col-12 mb-3">
                            <input type="text" className="form-control" placeholder="Weight (gms)" name="weight" value={cartformData.weight} onChange={(e) => handleFormChange(e, setCartFormData)} />
                        </div>
                        <div className="col-12 mb-3">
                            <textarea className="form-control" placeholder="Remarks" name="remarks" value={cartformData.remarks} onChange={(e) => handleFormChange(e, setCartFormData)}></textarea>
                        </div>
                    </div>
                    <Modal.Footer>
                        <button type="button" className="main-btn ml2 closebtn" onClick={handleCloseCart}>Close</button>
                        <button type="submit" className="main-btn mr2">Submit</button>
                    </Modal.Footer>
                </form>
            </Modal.Body>
          </Modal>
        </>
      )}
    </>
  );
}

export default Category;